import type {OFFER_LETTER} from 'src/types/crm';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {Table} from '@spaced-out/ui-design-system/lib/components/Table';
import {Banner} from '@spaced-out/ui-design-system/lib/components/Banner';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {ButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import User from 'src/components/crm/lib/user/user';
import {formatMomentDateAndTime} from 'src/utils/date-time';

import {
  SubTitleMedium,
  BodyMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';

import css from '../approve-offer-form.css';
import {type MenuOption} from '@spaced-out/ui-design-system';


type ApprovalObject = {
  createdOn?: string;
  updatedOn: string;
  approverId: string;
  updatedBy: string;
};

export const BasicDetails = ({
  offerDetails,
  isRejected,
  isApproved,
  currentApprovalObject,
}: {
  offerDetails: OFFER_LETTER | null;
  isRejected: boolean;
  isApproved: boolean;
  currentApprovalObject: ApprovalObject | null;
}) => {
  const {t, language} = useLabelTranslation();
  const {approvalChain, id} = offerDetails ?? {};

  // @ts-expect-error - TS18048 - 'approvalChain' is possibly 'undefined'.
  const isApprovalChainConfigured = approvalChain.steps.length ?? false;

  function getBaseUrl(url: any) {
    try {
      const parsedUrl = new URL(url);
      return `${parsedUrl.protocol}//${parsedUrl.host}`;
    } catch (error: any) {
      return null;
    }
  }

  const baseUrl =
    getBaseUrl(window.location.href) || 'https://trm-dev.sensehq.com';

  const formatLakh = (num: any) => {
    const lakhValue = (num / 100000).toFixed(2);
    return lakhValue.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ' lakh';
  };

  const formatNumber = (value: undefined | string, format: string) => {
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    const num = parseFloat(value);
    if (isNaN(num)) {
      return '-';
    }

    const parts = String(value).split('.');
    const decimalPlaces = parts.length > 1 ? parts[1].length : 0;
    const decimalOptions = {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    };
    switch (format) {
      case 'thousand':
        return num.toLocaleString('en-IN', decimalOptions);
      case 'lakh':
        return num.toLocaleString('en-IN', decimalOptions);
      default:
        return num.toString();
    }
  };

  // @ts-expect-error - TS7006 - Parameter 'fields' implicitly has an 'any' type.
  const calculateValues = (fields) => {
    const result: Record<string, any> = {};

    // @ts-expect-error - TS7006 - Parameter 'field' implicitly has an 'any' type.
    fields.forEach((field) => {
      if (
        field.value && // Check if value is present and not empty
        field.value !== '' &&
        field.value !== null &&
        field.value !== undefined
      ) {
        // Format number values based on format type
        if (
          ['NUMBER', 'DECIMAL', 'FORMULA'].includes(field.fieldType) &&
          field.format
        ) {
          result[field.description] = formatNumber(field.value, field.format);
        }
        // Directly add date values
        else if (field.fieldType === 'DATE') {
          result[field.description] = field.formattedDate || field.value;
        }
        // For text type values
        else if (field.fieldType === 'TEXT') {
          result[field.description] = field.value;
        }
        // If it's a formula without a direct value, leave it as '-'
        else {
          result[field.description] =
            field.value !== null && field.value !== undefined
              ? field.value
              : '-';
        }
      }
    });

    return result;
  };

  const formattedValues = calculateValues(offerDetails?.placeholders ?? []);

  const handleSelectedDownLoadType = (option: MenuOption) => {
    if (option.key === 'pdf') {
      window.open(
        `${baseUrl}/trmapi/api/offer-letter/download-generated/${id}`,
        '_blank',
      );
    } else if (option.key === 'docx') {
      window.open(
        `${baseUrl}/trmapi/api/offer-letter/download/${id}`,
        '_blank',
      );
    }
  };

  const renderStatusAlert = () => {
    if (isRejected) {
      return (
        // @ts-expect-error TS2769
        <InContextAlert leftIconName="shield-exclamation" semantic="danger">
          {t('Rejected_by', 'Rejected by')}:{' '}
          {/* @ts-expect-error - TS2769 - No overload matches this call. | TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'. */}
          <User userId={parseInt(currentApprovalObject?.updatedBy)} />
          {`, ${t('on', 'on')}`}{' '}
          {formatMomentDateAndTime(currentApprovalObject?.updatedOn, language)}
        </InContextAlert>
      );
    }

    if (isApproved) {
      return (
        // @ts-expect-error TS2769
        <InContextAlert leftIconName="circle-check" semantic="success">
          {t('Approved_by', 'Approved by')}:{' '}
          {/* @ts-expect-error - TS2769 - No overload matches this call. | TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'. */}
          <User userId={parseInt(currentApprovalObject?.approverId)} />
          {`, ${t('on', 'on')}`}{' '}
          {formatMomentDateAndTime(currentApprovalObject?.updatedOn, language)}
        </InContextAlert>
      );
    }

    return (
      <div className={css.taskRow}>
        <Icon name="info-circle" color="secondary" />
        <BodyMedium color="secondary">
          {offerDetails
            ? `${t(
                'Offer_Approval_requested_on',
                'Offer Approval requested on',
              )}: ${new Date(offerDetails.updatedOn).toLocaleString(language)}`
            : null}
        </BodyMedium>
      </div>
    );
  };
  return (
    <div className={css.basicDetailsContainer}>
      {renderStatusAlert()}
      <div className={css.basicDetailsOfferLetter}>
        <SubTitleMedium>{t('Offer_Letter', 'Offer Letter')}</SubTitleMedium>
        <div className={css.taskRow}>
          <Icon name="file-pdf" size="large" />
          {offerDetails?.convertedFilename ? (
            <Link
              to={`${baseUrl}/trmapi/api/offer-letter/download-generated/${id}`}
              target="_blank"
              as="buttonTextMedium"
            >
              {offerDetails.convertedFilename}
            </Link>
          ) : null}
          <ButtonDropdown
            menu={{
              isFluid: false,
              menuDisabled: false,
              options: [
                {
                  key: 'pdf',
                  label: t('Download_as_PDF', 'Download as PDF'),
                },
                {
                  key: 'docx',
                  label: t('Download_as_Docx', 'Download as Docx'),
                },
              ],
              size: 'medium',
            }}
            onOptionSelect={handleSelectedDownLoadType}
            size="medium"
            tooltip={{
              body: t(
                'Download_as_ellipsisvertical',
                'Download as ellipsis-vertical',
              ),
            }}
            type="tertiary"
            iconLeftName="ellipsis-vertical"
          />
        </div>
      </div>

      <div>
        <Table
          borderRadius="12px"
          entries={Object.keys(formattedValues ?? []).map((comp, index) => ({
            [comp]: formattedValues[comp],
            value: formattedValues[comp],
            labels: comp,
            id: index,
          }))}
          headers={[
            {
              key: 'labels',
              label: t('Offer_Attribute', 'Offer Attribute'),
            },
            {
              key: 'value',
              label: t('Details', 'Details'),
            },
          ]}
        />
      </div>
    </div>
  );
};
